import React, { useContext, useEffect, useMemo, useRef } from "react";

import CustomMapLayer, {
  CUSTOM_MAPS_SOURCE_ID_PREFIX,
  getInteractiveLayerById,
} from "../layers/customMap";
import {
  EventFeature,
  findRelevantFeatures,
  LayerConfig,
  LayerHook,
  LayerHookBuilder,
  LayerId,
} from "./types";
import { CustomMap, LayerContext, LayerContextType } from "../layers";
import { flatten, uniqBy } from "lodash";
import { MapEvent } from "react-map-gl";

type CustomMapLayerValue = {
  customMapId?: Maybe<string>;
  geometryId?: Maybe<string>;
};

export type CustomMapLayerConfig = LayerConfig<CustomMapLayerValue> & {
  maps: Array<CustomMap>;
};

type GeometryRef = Pick<EventFeature, "id" | "source" | "sourceLayer">;

type SelectFeatureProps = {
  event: MapEvent;
  allInteractiveLayerIds: LayerId[];
};

const selectFeature = ({
  event,
  allInteractiveLayerIds,
}: SelectFeatureProps) => {
  // there could be multiple layers displaying a feature, so we dedupe by id
  const features = uniqBy(
    findRelevantFeatures(event, allInteractiveLayerIds),
    "id"
  );

  return features[0];
};

export const getInteractiveLayerIds = (
  maps: CustomMap[],
  isLayerVisible: LayerContextType["isLayerVisible"]
) => {
  return flatten(
    maps
      .filter(map => map.isObject)
      .map(map => map.id)
      .filter(id => isLayerVisible({ group: "customMaps", id }))
      .map(getInteractiveLayerById)
  );
};

const customMapsHook: LayerHookBuilder<CustomMapLayerConfig> = ({
  config,
  mapLoaded,
  helpers,
}) => {
  const hoveredGeometry = useRef<Maybe<GeometryRef>>(null);
  const clickedGeometry = useRef<Maybe<GeometryRef>>(null);
  const { isLayerVisible } = useContext(LayerContext);

  const allInteractiveLayerIds = getInteractiveLayerIds(
    config.maps,
    isLayerVisible
  );

  useEffect(() => {
    if (!mapLoaded) {
      return;
    }

    if (clickedGeometry.current) {
      helpers.emit({
        type: "removeFeatureState",
        data: {
          field: "click",
          ...clickedGeometry.current,
        },
      });
    }

    if (config.value?.customMapId && config.value.geometryId) {
      const geometryFeature = allInteractiveLayerIds
        .map(
          layerId =>
            helpers.featuresByFilter({
              layer: layerId,
              filter: ["==", "id", config.value?.geometryId],
            })[0]
        )
        .find(feature => feature);

      if (geometryFeature) {
        helpers.emit({
          type: "setFeatureState",
          data: {
            id: geometryFeature.id,
            field: "click",
            source: geometryFeature.source,
            sourceLayer: geometryFeature.sourceLayer,
          },
        });

        clickedGeometry.current = geometryFeature;
      }
    }
  }, [config.value?.customMapId, config.value?.geometryId, mapLoaded]);

  return useMemo<LayerHook>(() => {
    return {
      interactiveLayerIds: allInteractiveLayerIds,
      zIndex: 2,
      render: () => {
        return config.maps.map(map => (
          <CustomMapLayer
            id={map.id}
            tileset={map.tileset}
            key={map.id}
            labelsVisibleOnMap={map.labelsVisibleOnMap}
            isObject={map.isObject}
          />
        ));
      },
      canHandleHover: () => !!config.interactive?.hover,
      onHover: ({ event }) => {
        if (hoveredGeometry.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredGeometry.current },
          });
          hoveredGeometry.current = null;
        }

        const feature = selectFeature({
          event,
          allInteractiveLayerIds,
        });

        if (feature) {
          helpers.emit({
            type: "setFeatureState",
            data: {
              id: feature.id,
              field: "hover",
              source: feature.source,
              sourceLayer: feature.sourceLayer,
            },
          });

          helpers.setCursor("pointer");
          // This should always be present, but in case it's not,
          // let's not display an empty tooltip
          if (feature.properties.label) {
            helpers.emit({
              type: "setTooltip",
              data: {
                ...event.offsetCenter,
                text: feature.properties.label,
              },
            });
          }
          hoveredGeometry.current = {
            id: feature.id,
            source: feature.source,
            sourceLayer: feature.sourceLayer,
          };
        }
      },
      onFocusLost: () => {
        if (hoveredGeometry.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredGeometry.current },
          });
          hoveredGeometry.current = null;
        }
      },
      canHandleClick: event =>
        !!config.interactive?.click && !!event.features?.length,
      onClick: ({ event }) => {
        const feature = selectFeature({
          event,
          allInteractiveLayerIds,
        });

        if (!feature) {
          return false;
        }

        if (
          clickedGeometry.current &&
          clickedGeometry.current.id !== feature.id
        ) {
          helpers.emit({
            type: "removeFeatureState",
            data: {
              field: "click",
              ...clickedGeometry.current,
            },
          });
        }

        clickedGeometry.current = {
          id: feature.id,
          source: feature.source,
          sourceLayer: feature.sourceLayer,
        };
        helpers.emit({
          type: "setFeatureState",
          data: {
            id: feature.id,
            field: "click",
            source: feature.source,
            sourceLayer: feature.sourceLayer,
          },
        });

        config.onClick?.({
          customMapId: (feature.source as string).replace(
            CUSTOM_MAPS_SOURCE_ID_PREFIX,
            ""
          ),
          geometryId: feature.id,
        });
        return true;
      },
    };
  }, [config]);
};

export default customMapsHook;
